import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../api';
import { canWrite, useAuth } from '../auth';
import { useSuppliers } from '../queries';
import { Button, Card, Empty, ErrorMessage, Field, Notice, PageHeader, Select, Spinner, money, number, plural } from '../ui';

interface Row {
  ruleId: number;
  itemId: number;
  itemNo: string;
  itemName: string;
  colorName: string;
  condition: string;
  available: number;
  onOrder: number;
  minQty: number;
  targetQty: number;
  suggested: number;
  supplierId: number | null;
  supplierName: string | null;
  lastPrice: string | null;
  estCost: string | null;
  soldPerMonth: number | null;
}

/** Items below their reorder level, and a purchase order made from the lines you choose. */
export function BuyList() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const q = useQuery({ queryKey: ['buy-list'], queryFn: () => api<{ rows: Row[] }>('/api/v1/buy-list').then((r) => r.rows) });
  const suppliers = useSuppliers();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [supplier, setSupplier] = useState('');
  const make = useMutation({
    mutationFn: () => api<{ order: { purchase: { id: number } } }>('/api/v1/buy-list/order', { body: { supplierId: Number(supplier), ruleIds: [...picked], orderedOn: new Date().toISOString().slice(0, 10) } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['buy-list'] });
      void qc.invalidateQueries({ queryKey: ['purchases'] });
      const id = (r.order as { purchase?: { id: number }; id?: number }).purchase?.id ?? (r.order as { id?: number }).id;
      navigate(id ? `/purchases/${id}` : '/purchases');
    },
  });
  const rows = q.data ?? [];
  const total = rows.filter((r) => picked.has(r.ruleId)).reduce((t, r) => t + Number(r.estCost ?? 0), 0);
  const toggle = (id: number) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <>
      <PageHeader title="Buy list" subtitle="Items whose available stock plus what is on order is under their reorder level. Set levels on a lot's page." />
      <div className="space-y-4">
        {q.isPending && <Spinner />}
        {q.error && <ErrorMessage error={q.error} />}
        {q.data && rows.length === 0 && <Empty>Nothing is below its reorder level. Set levels on a lot to see it here.</Empty>}
        {rows.length > 0 && (
          <Card
            actions={
              writable ? (
                <div className="flex flex-wrap items-end gap-2">
                  <Field label="Order from">
                    <Select value={supplier} onChange={(e) => setSupplier(e.target.value)}>
                      <option value="">Choose a supplier…</option>
                      {(suppliers.data ?? []).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Button disabled={!supplier || picked.size === 0 || make.isPending} onClick={() => make.mutate()}>
                    Make a purchase order ({plural(picked.size, 'line')}{total > 0 ? `, about ${money(total.toFixed(2))}` : ''})
                  </Button>
                </div>
              ) : undefined
            }
          >
            <ErrorMessage error={make.error} />
            <div className="overflow-x-auto">
              <table className="data w-full min-w-[56rem] text-sm">
                <thead>
                  <tr>
                    {writable && (
                      <th className="w-8">
                        <input type="checkbox" aria-label="Choose all" className="size-4" checked={picked.size === rows.length} onChange={(e) => setPicked(e.target.checked ? new Set(rows.map((r) => r.ruleId)) : new Set())} />
                      </th>
                    )}
                    <th>Item</th>
                    <th>Colour</th>
                    <th>Cond.</th>
                    <th className="text-right">Available</th>
                    <th className="text-right">On order</th>
                    <th className="text-right">Reorder at</th>
                    <th className="text-right">To buy</th>
                    <th>Supplier</th>
                    <th className="text-right">Last price</th>
                    <th className="text-right">Market / month</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.ruleId}>
                      {writable && (
                        <td>
                          <input type="checkbox" className="size-4" aria-label={`Choose ${r.itemNo} ${r.colorName}`} checked={picked.has(r.ruleId)} onChange={() => toggle(r.ruleId)} />
                        </td>
                      )}
                      <td>
                        <Link to={`/items/${r.itemId}?tab=vendors`} className="font-medium hover:underline">
                          {r.itemNo}
                        </Link>
                        <div className="max-w-56 truncate text-xs text-slate-500">{r.itemName}</div>
                      </td>
                      <td>{r.colorName}</td>
                      <td>{r.condition === 'N' ? 'New' : 'Used'}</td>
                      <td className="text-right tabular-nums">{number(r.available)}</td>
                      <td className="text-right tabular-nums">{number(r.onOrder)}</td>
                      <td className="text-right tabular-nums">{number(r.minQty)}</td>
                      <td className="text-right font-medium tabular-nums">{number(r.suggested)}</td>
                      <td>{r.supplierName ?? <span className="text-slate-400">none chosen</span>}</td>
                      <td className="text-right tabular-nums">{r.lastPrice ? money(r.lastPrice) : '—'}</td>
                      <td className="text-right tabular-nums">{r.soldPerMonth ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Notice>The purchase order uses what the chosen supplier last charged for each line (nothing, if they have not sold it to you before: change the price on the order). Nothing is placed with the supplier by BrickLoop; the order is your record.</Notice>
          </Card>
        )}
      </div>
    </>
  );
}
